import AccessAlarmOutlinedIcon from '@mui/icons-material/AccessAlarmOutlined';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { FC, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';

import { Application } from '@features/applications';
import ApplicationDocsListItem from '@features/applicationsDocs/components/ApplicationDocsListItem/ApplicationDocsListItem';
import UploadApplicationDocModal from '@features/applicationsDocs/components/UploadApplicationDocModal/UploadApplicationDocModal';
import { useGetApplicationDocs } from '@features/applicationsDocs/hooks/useGetApplicationDocs';
import { ApplicationDocument } from '@features/applicationsDocs/type';
import { downloadAllDocumentsRoute } from '@features/digitalOnboarding';
import ResendDocumentModal from '@features/digitalOnboarding/components/ResendDocumentModal/ResendDocumentModal';
import SendReminderModal from '@features/digitalOnboarding/components/SendReminderModal/SendReminderModal';
import TemplateListItem from '@features/digitalOnboarding/components/TemplateListItem/TemplateListItem';
import { useGetSignedTemplates } from '@features/digitalOnboarding/hooks/useGetSignedTemplates';
import { SignedTemplate } from '@features/digitalOnboarding/types';
import { useGetCurrentPlan } from '@features/payments/hooks/useGetCurrentPlan';
import { bold } from '@styles/fontWeights';
import { white } from '@styles/colors';
import RestTemplateList from '../RestTemplateList/RestTemplateList';
import { getCookie } from '@utils/getCookie';
import axios from 'axios';
import JSZip from 'jszip';
import { LoadingButton } from '@mui/lab';
import { authContext } from '@staffedupusa/shared-components';
import { Send, Share } from '@mui/icons-material';
import { useResendDocument } from '@features/digitalOnboarding/hooks/useResendDocument';
import { toast } from 'react-toastify';
import { useSendOnboardingDocuments } from '@features/digitalOnboarding/hooks/useSendOnboardingDocuments';
import { selectedPlanName } from '@utils/pricing';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';

type DigitalOnboardingTabProps = {
  application: Application;
};

const DigitalOnboardingTab: FC<DigitalOnboardingTabProps> = ({
  application,
}) => {
  const { authData } = useContext(authContext);
  const isCompany = authData.user?.company;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [isSendReminderModalOpen, setIsSendReminderModalOpen] = useState(false);
  const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
    useState(false);
  const [isPostDocumentModal, setIsPostDocumentModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  const [errorText, setErrorText] = useState('');
  const { sendDocument, isSendingDocument } = useResendDocument({
    setErrorText,
  });
  const { sendOnboardingDocument, isSendingOnboardingDocument } =
    useSendOnboardingDocuments({
      setErrorText,
    });

  const navigate = useNavigate();

  const handleGoToOnboarding = () => {
    isCompany &&
      track(
        `${amplitudeEventsName.APPLICANTS_CLICK_ONBOARDING_SETTINGS}-${selectedPlan}`,
      );
    navigate(
      authData?.user?.enterprise
        ? '/enterprise/onboarding'
        : '/employer/account/onboarding',
      { state: { onboarding: true } },
    )
  };
  const handleGoToOnBilling = () => {
    isCompany &&
      track(
        `${amplitudeEventsName.APPLICANTS_CLICK_TURN_ON_ONBOARDING}-${selectedPlan}`,
      );
    navigate('/employer/account#Manage-Billing');
  };

  const { currentPlanData, isCurrentPlanLoading } = useGetCurrentPlan(
    authData?.user?.enterprise
      ? { companyId: application?.job_listing?.company?.id }
      : undefined,
  );

  const { signedTemplatesData, isSignedTemplatesLoading } =
    useGetSignedTemplates({
      applicationId: application.id,
      enabled: Boolean((currentPlanData?.object?.plan_int === 2) && application?.hired),
    });
  const { applicationDocsData } = useGetApplicationDocs({
    applicationId: application.id,
    enabled: Boolean((currentPlanData?.object?.plan_int === 2) && application?.hired),
  });

  const signedTemplates: SignedTemplate[] = signedTemplatesData?.object || [];

  const handleDownloadApplication = async () => {
    setDownloadLoading(true);
    const documentsDownloadLink = `${import.meta.env.VITE_API_BASE_URL}ext${downloadAllDocumentsRoute}${application?.id}/`;

    const token = getCookie('SU_AUTH');
    const crsfToken = getCookie('csrftoken');

    try {
      const response = await axios.get(documentsDownloadLink, {
        headers: {
          Authorization: `Token ${token}`,
          'X-CSRFToken': crsfToken,
        },
      });
      if (response.status === 200 && response.data.url) {
        const fileDownloadUrl = response.data.url;

        const zipResponse = await axios.get(fileDownloadUrl, {
          responseType: 'blob',
        });

        await downloadZipFile(zipResponse.data);
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error('Error during file download:', error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const downloadZipFile = async (blobData: string | ArrayBuffer | Blob) => {
    const zip = new JSZip();

    try {
      const newZip = await zip.loadAsync(blobData);

      const zipFileName = `${application?.applicant?.name}.zip`;
      const zipBlob = await newZip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = zipFileName;
      link.click();
    } catch (error) {
      console.error('Error while generating zip:', error);
    }
  };

  const handleSend = () =>
    sendDocument(
      {
        application_id: application?.id,
        template_ids: signedTemplates.map((item) => item.id),
      },
      {
        onSuccess: () => {
          toast.success('Document sent to the applicant');
        },
      },
    );

  const handleSendOnboardingDocuments = () => {
    isCompany &&
      track(
        `${amplitudeEventsName.APPLICANTS_CLICK_SEND_ONBOARDING_DECUMENTS}-${selectedPlan}`,
      );
    const options = {
      onSuccess: (data: { message: string; status: number }) => {
        if (data.status === 200) {
          toast.success(data.message);
        } else {
          toast.error(data?.message || 'Something Went Wrong');
        }
      },
    };
    sendOnboardingDocument({ application_id: application?.id }, options);
  };

  if (isSignedTemplatesLoading || isCurrentPlanLoading) {
    return (
      <Stack spacing={3} height="100%" bgcolor={white} p={4} borderRadius={8}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          textAlign="center"
          bgcolor={white}
          width="100%"
          borderRadius={8}
        >
          <CircularProgress color="success" />
        </Box>
      </Stack>
    );
  }

  if (!currentPlanData?.object || currentPlanData?.object?.plan_int !== 2) {
    return (
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        height="100%"
        p={{ xs: 3 }}
      >
        <Typography
          variant="h3"
          color="secondary"
          textAlign="center"
          style={{
            fontWeight: 'bold',
            fontSize: '24px',
            marginBottom: '3px',
          }}
        >
          Want to effortlessly onboard new hires in seconds?
        </Typography>
        <Typography variant="body1" textAlign="center" fontSize="16px">
          Send job offers that include your entire new hire paperwork package
          with just a click, and get new hires on your schedule in a fraction of
          the time.
        </Typography>
        {authData?.user?.company ? (
          <Button
            variant="contained"
            onClick={handleGoToOnBilling}
            style={{ borderRadius: '6px', marginTop: '20px' }}
          >
            Turn on Digital Onboarding Now
          </Button>
        ) : null}
      </Stack>
    );
  }
  if (currentPlanData?.object?.plan_int === 2 && (!application?.hired || (application?.hired && application.application_actions.hired_action > 0))) {
    return (
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <Typography variant="h3" color="secondary" textAlign="center">
          Once the applicant accepts an offer for this position, you can view
          their completed documentation here.
        </Typography>
        <Typography variant="body1" textAlign="center">
          Need to adjust your paperless onboarding?
        </Typography>
        <Button variant="contained" onClick={handleGoToOnboarding}>
          Take me to my digital onboarding settings
        </Button>
      </Stack>
    );
  }

  if (signedTemplates.length === 0) {
    return (
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <Typography variant="h3" color="secondary" textAlign="center">
          The applicant has been successfully hired, but their onboarding
          documents is not yet available
        </Typography>
        <Typography variant="body1" textAlign="center">
          Need to adjust your paperless onboarding?
        </Typography>
        <Button variant="contained" onClick={handleGoToOnboarding}>
          Take me to my digital onboarding settings
        </Button>
        <LoadingButton
          loading={isSendingOnboardingDocument}
          variant="contained"
          onClick={handleSendOnboardingDocuments}
        >
          Send Onboarding Documents
        </LoadingButton>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} height="100%" bgcolor={white} p={4} borderRadius={8}>
      <Stack spacing={3} direction={{ xs: 'column', md: 'row' }}>
        {/* Version 2 Functionality of add Hire Document */}
        <Button
          variant="contained"
          size="small"
          sx={{ fontSize: '15px !important' }}
          startIcon={<TextSnippetOutlinedIcon />}
          onClick={() => setIsPostDocumentModal(true)}
        >
          Send Post Hire documents
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ fontSize: '15px !important' }}
          startIcon={<NoteAddOutlinedIcon />}
          onClick={() => setIsUploadDocumentModalOpen(true)}
        >
          Additional Documents Upload
        </Button>

        {Boolean(
          signedTemplates.length ===
            signedTemplates.filter((item) => item.file_path).length,
        ) && (
          <LoadingButton
            variant="contained"
            size="small"
            sx={{ fontSize: '15px !important' }}
            color="warning"
            loading={downloadLoading}
            startIcon={<DownloadForOfflineOutlinedIcon />}
            onClick={handleDownloadApplication}
          >
            Download All Documents
          </LoadingButton>
        )}
      </Stack>
      <Box px={2}>
        <Typography color="secondary.light" fontWeight={bold}>
          {`${signedTemplates.filter((item) => item.file_path).length}/${signedTemplates.length} Documents Complete`}
        </Typography>
        <Stack
          spacing={3}
          direction={{ xs: 'column', md: 'row' }}
          alignItems="center"
        >
          <LinearProgress
            color="success"
            variant="determinate"
            value={
              (signedTemplates.filter((item) => item.file_path).length /
                signedTemplates.length) *
              100
            }
            sx={{
              width: { xs: '100%', md: 'auto' },
              height: 10,
              borderRadius: 10,
              flexGrow: 1,
            }}
          />
          <Button
            variant="contained"
            size="small"
            color="warning"
            disabled={Boolean(
              signedTemplates.length ===
                signedTemplates.filter((item) => item.file_path).length,
            )}
            startIcon={<AccessAlarmOutlinedIcon />}
            onClick={() => setIsSendReminderModalOpen(true)}
          >
            Send a Reminder
          </Button>
        </Stack>
      </Box>
      <Stack spacing={2}>
        {signedTemplates.map((template: SignedTemplate) => (
          <TemplateListItem
            key={template.id}
            template={template}
            handleResend={() => setSelectedTemplateId(template.id)}
            // filePath={
            //   (signedTemplates.find(
            //     (signedTemplate: SignedTemplate) =>
            //       signedTemplate.template.id === template.id,
            //   )?.file_path as string) || null
            // }
            filePath={template.file_path}
          />
        ))}
        <LoadingButton
          onClick={handleSend}
          loading={isSendingDocument}
          disabled={isSendingDocument}
          startIcon={<Send />}
          variant="contained"
          sx={{ alignSelf: 'flex-end', width: '200px', margin: '5px auto' }}
        >
          Resend All
        </LoadingButton>
      </Stack>
      <Stack spacing={2}>
        {Boolean(applicationDocsData?.object?.length) && (
          <>
            <Divider />
            <Typography
              color="secondary.light"
              fontWeight={bold}
              textAlign="center"
            >
              Additional documents
            </Typography>
          </>
        )}
        {applicationDocsData?.object?.map(
          (applicationDoc: ApplicationDocument) => (
            <ApplicationDocsListItem
              key={applicationDoc.id}
              document={applicationDoc}
              applicationId={application.id}
            />
          ),
        )}
      </Stack>
      <SendReminderModal
        isOpen={isSendReminderModalOpen}
        handleClose={() => setIsSendReminderModalOpen(false)}
        applicationId={application.id}
      />
      <ResendDocumentModal
        templateId={selectedTemplateId}
        handleClose={() => setSelectedTemplateId(null)}
        applicationId={application.id}
      />
      <UploadApplicationDocModal
        isOpen={isUploadDocumentModalOpen}
        handleClose={() => setIsUploadDocumentModalOpen(false)}
        applicationId={application.id}
      />
      <RestTemplateList
        isOpen={isPostDocumentModal}
        handleClose={() => setIsPostDocumentModal(false)}
        applicationId={application.id}
        templeteIds={
          signedTemplates ? signedTemplates.map((item) => item.template.id) : []
        }
      />
    </Stack>
  );
};

export default DigitalOnboardingTab;
